import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Form } from '@unform/web';
import { format, parseISO } from 'date-fns';

import api from 'services/api';
import { Avaliacaoeng, Role } from 'utils/variables';
import PendenteAvaliacao from 'assets/PendenteAvaliacao.svg';
import { show } from 'store/modules/dialog/actions';
import { IMidiaInfoItem, midiainfo } from '../../utils/midiainfo/index';
import { Comercial } from 'pages/@types';

import { Container, Scroll } from './styles';
import { Table, Td } from 'styles/global';

import Card from 'components/Card';
import Button from 'components/Button';
import Avatar from 'components/Avatar';
import Input from 'components/Input';
import DropDownMenu from 'components/DropDownMenu';
import PlayerBaixa from 'components/PlayerBaixa';
import Loader from 'components/ContentLoader/GenericLoader';
import Tooltip from 'components/Tooltip';
import NotFound from 'components/NotFound';
import Pagination from 'components/Pagination';

import {
  FiSearch,
  FiMessageSquare,
  FiActivity,
  FiPlay,
  FiInfo,
  FiVideo,
  FiCheckCircle,
} from 'react-icons/fi';
import AppError from 'utils/AppError';

export default function ComerciaisReprovados() {
  // react-router-dom
  const navigate = useNavigate();

  const [comerciais, setComerciais] = useState<Comercial[]>([]);
  const [loading, setLoading] = useState(false);
  const [player, setPlayer] = useState({
    open: false,
    midia_url: '',
    pipe: false,
  });
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [filtro, setFiltro] = useState('');
  const [selected, setSelected] = useState<Comercial>({} as Comercial);

  const dispatch = useDispatch();
  const { profile } = useSelector((state: any) => state.usuario);
  const { callback: callbackDialog } = useSelector(
    (state: any) => state.dialog,
  );

  // MSG DE OBS EM JANELA
  const handleObservacao = useCallback(
    (observacao: string) => {
      const contentHTML = `<p>Observação: ${observacao}</p>`;
      const options = {
        type: 'info',
        contentHTML,
        showConfirmButton: false,
        cancelButtonText: 'FECHAR',
      };
      dispatch(show(options));
    },
    [dispatch],
  );

  // fecha player
  const closeVideo = useCallback(() => {
    setPlayer({
      open: !player.open,
      midia_url: '',
      pipe: false,
    });
  }, [player.open]);

  const handlePlayer = useCallback(
    preview_url => {
      if (preview_url) {
        setPlayer({
          open: !player.open,
          midia_url: preview_url,
          pipe: false,
        });
      } else {
        dispatch(
          show({
            type: 'warning',
            text: 'Preview indisponível no momento',
            showConfirmButton: false,
          }),
        );
      }
    },
    [dispatch, player.open],
  );

  // confirmar check
  const handleResponsavel = useCallback(
    (comercial: Comercial) => {
      try {
        const option = {
          type: 'question',
          text: `Confirmar responsável de ${comercial.titulo}?`,
          confirmButtonText: 'CONFIRMAR',
          callback: {
            action: 'responsavelopec',
            data: {
              responsavelopec_id: profile.id,
            },
            url: `/comerciaists/${comercial.id}`,
            method: api.put,
          },
          haveCallback: true,
          successMessage: 'Confirmado execução!',
          cancelButtonText: 'NÃO',
          showCancelButton: false,
          showConfirmButton: true,
          showCloseButton: true,
        };
        setSelected(comercial);
        dispatch(show(option));
      } catch (e) {
        if (e instanceof Error) toast.error(e.message);
        else if (e instanceof AppError) toast.error(e.message);
        else {
          const appError = e as AppError;
          if (appError.message)
            toast.error(appError.message, {
              toastId: appError.message,
            });
          else toast.error('Erro inesperado');
        }
      }
    },
    [dispatch, profile.id],
  );

  const handleReavaliar = useCallback(
    (comercial: Comercial) => {
      try {
        if (comercial.midia) {
          const option = {
            type: 'question',
            text: `Solicitar reavaliação de ${comercial.titulo}?`,
            confirmButtonText: 'CONFIRMAR',
            callback: {
              action: 'reavaliar',
              data: {
                avaliacaoeng: Avaliacaoeng.PENDENTE,
              },
              url: `/midias/${comercial.midia.id}`,
              method: api.put,
            },
            haveCallback: true,
            successMessage: 'Confirmado execução!',
            cancelButtonText: 'NÃO',
            showCancelButton: false,
            showConfirmButton: true,
            showCloseButton: true,
          };
          setSelected(comercial);
          dispatch(show(option));
        } else {
          toast.error('Erro inesperado');
        }
      } catch (e) {
        if (e instanceof Error) toast.error(e.message);
        else if (e instanceof AppError) toast.error(e.message);
        else {
          const appError = e as AppError;
          if (appError.message)
            toast.error(appError.message, {
              toastId: appError.message,
            });
          else toast.error('Erro inesperado');
        }
      }
    },
    [dispatch],
  );

  function handleMidiaInfoDialog(
    midiainfovalue: IMidiaInfoItem,
    statuspreavaliacao: string,
  ) {
    const options = midiainfo(statuspreavaliacao, midiainfovalue);
    dispatch(show(options));
  }

  async function handleActionSelect(
    comercial: Comercial,
    action: { value: number },
  ) {
    switch (action.value) {
      case 1:
        if (comercial.midia) handlePlayer(comercial.midia.preview_url);
        break;
      case 2:
        if (comercial.midiainfo && comercial.midia)
          handleMidiaInfoDialog(
            comercial.midiainfo.midiainfo8ch,
            comercial.midia.statuspreavaliacao8ch,
          );
        break;
      case 3:
        handleReavaliar(comercial);
        break;
      default:
        break;
    }
  }
  useEffect(() => {
    const controller = new AbortController();
    async function loadReprovados() {
      setLoading(true);
      try {
        const response = await api.get(`/comerciaists/comerciaisreprovados`, {
          signal: controller.signal,
          params: { page, ...(filtro && { search: filtro }) },
        });
        if (response && response.data) {
          setComerciais(response.data[0]);
          setPages(response.data[1]);
        }

        setLoading(false);
      } catch (e) {
        setLoading(false);
        if (e instanceof Error) toast.error(e.message);
        else toast.error('Erro inesperado');
      }
    }
    loadReprovados();
    return () => {
      controller.abort();
    };
  }, [filtro, page]);

  useEffect(() => {
    if (callbackDialog && callbackDialog.success) {
      if (callbackDialog.success.action === 'responsavelopec') {
        setComerciais(prevState =>
          prevState.map(c =>
            c.id === selected.id
              ? {
                  ...c,
                  responsavelopec: {
                    id: profile.id,
                    nome: profile.nome,
                    avatar_url: profile.avatar_url,
                  },
                }
              : c,
          ),
        );
      }
      if (callbackDialog.success.action === 'reavaliar') {
        setComerciais(prevState => prevState.filter(c => c.id !== selected.id));
      }
    }
  }, [
    callbackDialog,
    profile.avatar_url,
    profile.id,
    profile.nome,
    selected.id,
  ]);

  return (
    <Container>
      <Card>
        <header>
          <h4>LISTA DE COMERCIAIS REPROVADOS</h4>
          <Pagination
            total={pages}
            activePage={page}
            onClick={page => setPage(page)}
          />
          <div>
            <Form
              onSubmit={e => {
                setPage(1);
                setFiltro(e.searchLocal);
              }}
            >
              <Input
                id="filtro"
                name="searchLocal"
                placeholder="Buscar na Tabela"
                icon={<FiSearch />}
              />
              <Tooltip target="filtro" position="left">
                Buscas possíveis:
                <br />
                Pelo título, código de material ou pela data de reprova
                (dd/mm/yyyy)
                <br />
              </Tooltip>
            </Form>
          </div>
        </header>
        <Scroll>
          {loading ? (
            <Loader />
          ) : comerciais && comerciais.length ? (
            <Table>
              <thead>
                <tr>
                  <th style={{ width: 85 }}>CM</th>
                  <th>TITULO</th>
                  <th>AGENCIA</th>
                  <th>1ª VEIC.</th>
                  <th style={{ width: 160 }}>REPROVADO EM</th>
                  <th style={{ width: 100 }}>DURAÇÃO</th>
                  <th style={{ width: 108 }}>PTOVENDA</th>
                  <th style={{ width: 70, textAlign: 'center' }}>AVAL.</th>
                  <th style={{ width: 60 }}>OBS.</th>
                  <th style={{ width: 50 }}>RESP. </th>
                  <th style={{ width: 50 }} />
                </tr>
              </thead>

              <tbody>
                {!!comerciais.length &&
                  comerciais.map(
                    (comercial: Comercial) =>
                      comercial.midia && (
                        <tr key={comercial.id}>
                          <Td>{comercial.codmaterial}</Td>
                          <Td>
                            {comercial.titulo}{' '}
                            <strong>({comercial.cliente.mnemonico})</strong>
                          </Td>
                          <Td>
                            {comercial.agencia
                              ? comercial.agencia.mnemonico
                              : 'SEM AGÊNCIA'}
                          </Td>
                          <Td>{comercial.primeiraveiculacao}</Td>
                          <Td>
                            {format(
                              parseISO(comercial.midia.checked_at),
                              'dd/MM/yyyy HH:mm',
                            )}
                          </Td>
                          <Td>{comercial.duracao} s</Td>
                          <Td>{comercial.ptovenda}</Td>
                          <Td style={{ textAlign: 'center' }}>
                            <Avatar
                              size="40"
                              name={comercial.midia?.avaliador?.nome}
                              src={comercial.midia?.avaliador?.avatar_url}
                              round
                            />
                          </Td>
                          <Td>
                            <Button
                              fill
                              color="font"
                              round
                              onClick={() =>
                                handleObservacao(
                                  comercial.midia
                                    ? comercial.midia.obseng
                                    : 'Avaliação não encontrada',
                                )
                              }
                            >
                              <FiMessageSquare />
                            </Button>
                          </Td>
                          <Td>
                            {comercial.responsavelopec ? (
                              <Avatar
                                size="40"
                                name={comercial.responsavelopec.nome}
                                src={comercial.responsavelopec.avatar_url}
                                round
                              />
                            ) : (
                              <Button
                                fill
                                color="font"
                                round
                                disabled={
                                  profile.role !== Role.OPEC &&
                                  profile.role !== Role.ADMIN
                                }
                                onClick={() => {
                                  handleResponsavel(comercial);
                                }}
                              >
                                <FiCheckCircle />
                              </Button>
                            )}
                          </Td>
                          <Td>
                            <DropDownMenu
                              id={`acoes${comercial.id}`}
                              disabled={false}
                              actions={[
                                {
                                  value: 0,
                                  label: 'HISTÓRICO',
                                  icon: <FiActivity />,
                                  href: `/historico/${comercial.id}`,
                                },
                                {
                                  value: 1,
                                  label: 'ASSISTIR',
                                  icon: <FiPlay />,
                                  disabled:
                                    !comercial.midia ||
                                    !comercial.midia.preview_url,
                                },
                                {
                                  value: 2,
                                  label: 'MIDIAINFO',
                                  icon: <FiInfo />,
                                  disabled: !comercial.midiainfo,
                                },
                                {
                                  value: 3,
                                  label: 'REAVALIAR',
                                  icon: <FiVideo />,
                                  disabled: !(
                                    profile.role === Role.ADMIN ||
                                    (profile.role === Role.ENG &&
                                      profile.emissoras?.some(
                                        (e: { sigla: string }) =>
                                          e.sigla === 'CUR',
                                      ))
                                  ),
                                },
                              ]}
                              handleClick={(action: any) =>
                                !action.disabled &&
                                handleActionSelect(comercial, action)
                              }
                            />
                          </Td>
                        </tr>
                      ),
                  )}
              </tbody>
            </Table>
          ) : (
            <NotFound img={PendenteAvaliacao} title="Comerciais Reprovados">
              Nenhum COMERCIAL REPROVADO encontrado
            </NotFound>
          )}
        </Scroll>
      </Card>
      <PlayerBaixa
        open={player.open}
        toggleModal={() => closeVideo()}
        pipe={player.pipe}
        url={player.midia_url}
      />
    </Container>
  );
}
